import React from "react";
import { Box, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { C } from "../common/theme";

type BannerTone = "neutral" | "success" | "warning" | "danger";

const TONES: Record<BannerTone, { bg: string; border: string; icon: string }> = {
  neutral: { bg: C.paper, border: C.line2, icon: C.ink2 },
  success: { bg: C.leafTint, border: "#BFD9C8", icon: C.leaf },
  warning: { bg: C.bone1, border: "#F2C5AF", icon: C.coralText },
  danger: { bg: C.dangerTint, border: "#E3B9C2", icon: C.danger },
};

/** A one-line notice across the page: icon, bold lead, text, optional action. */
export default function Banner({
  tone = "neutral",
  icon,
  title,
  children,
  action,
}: {
  tone?: BannerTone;
  icon?: React.ReactNode;
  title?: React.ReactNode;
  children?: React.ReactNode;
  action?: React.ReactNode;
}) {
  const t = TONES[tone];
  return (
    <Box
      role={tone === "danger" ? "alert" : "status"}
      sx={{
        display: "flex",
        alignItems: { xs: "flex-start", sm: "center" },
        flexWrap: { xs: "wrap", sm: "nowrap" },
        gap: 1.5,
        px: 1.75,
        py: 1.25,
        borderRadius: "8px",
        border: `1px solid ${t.border}`,
        bgcolor: t.bg,
      }}
    >
      <Box sx={{ color: t.icon, display: "flex", "& svg": { fontSize: 20 } }}>{icon ?? <InfoOutlinedIcon />}</Box>
      <Typography variant="body2" sx={{ flex: "1 1 200px", color: C.ink2, minWidth: 0 }}>
        {title && <Box component="b" sx={{ color: C.ink, mr: 0.5 }}>{title}</Box>}
        {children}
      </Typography>
      {action && <Box sx={{ flex: "0 0 auto", ml: { xs: 4, sm: 0 } }}>{action}</Box>}
    </Box>
  );
}
